import { useEffect, useState } from 'react'
import cx from 'classnames'

import { useProperties } from 'hooks/queries/companies/properties/useProperties'
import SharedStyles from '../ViewControls.module.scss'
import Styles from './Filter.module.scss'
import { BackButton } from '../common/BackButton/BackButton'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { PropertyFinder } from '../common/PropertyFinder/PropertyFinder'
import { DefaultType, Property } from 'api/clients/PropertyClient'
import { useFilterProperties } from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/Filter/hooks/useFilterProperties'
import { defaultFilterForProperty } from './utils'
import { FilterList } from './components/FilterList/FilterList'
import { useViewPreferencesStore } from '../../../stores/useViewPreferencesStore'
import { Operator, Join } from './constants'
import { filterGroupsWithoutCalendarProperty } from 'screens/Finder/components/views/CalendarView/utils'

export interface Operand {
  kind: 'attribute' | 'property'
  property_id?: number
  attribute?: string
}

export interface Condition {
  operand: Operand
  operator: Operator
  value: any // number | string | number[] | string[] | Date | Date[]
}

export interface Group {
  glue: Join
  conditions: (Condition | Group)[]
}

export interface Filters {
  glue: Join
  groups: Group[]
}

interface Props {
  onBackButtonPress?: () => void
  onRemovedAllFilters?: () => void
  initialProperty?: Property
}

export const Filter = ({ onBackButtonPress, initialProperty, onRemovedAllFilters }: Props) => {
  const { filesView } = useFinderContext()
  useProperties({}, { enabled: true }, { pushToStore: true })
  const [properties] = useFilterProperties({ excludedProperties: [DefaultType.LastOpened, DefaultType.Shared] })
  const { setOpenedViewPreference, openedViewPreference } = useViewPreferencesStore()
  const [filters, setFilters] = useState<Filters | undefined>(filesView?.schema.filters)

  const groups = filterGroupsWithoutCalendarProperty({ filesView, groups: filters?.groups })
  const hasFilters = filters && groups && groups.length > 0

  useEffect(() => {
    setFilters(filesView?.schema.filters)
  }, [filesView?.schema.filters])

  const addFilter = (property: Property) => {
    if (filters && filters.groups) {
      setFilters({
        ...filters,
        groups: [...filters.groups, defaultFilterForProperty(property)],
      })

      return
    }

    setFilters({
      glue: Join.And,
      groups: [defaultFilterForProperty(property)],
    })
  }

  useEffect(() => {
    if (initialProperty) addFilter(initialProperty)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (hasFilters && openedViewPreference === 'filter') {
    setOpenedViewPreference('filterPicked')
  }

  if (!hasFilters && openedViewPreference === 'filterPicked') {
    setOpenedViewPreference('filter')
  }

  return (
    <div className={cx(SharedStyles.control, Styles.filterControl)} data-testid="view-modifier-filter">
      <div className={cx(SharedStyles.title, { [SharedStyles.noBackButton]: !onBackButtonPress })}>
        {!!onBackButtonPress && <BackButton onPress={onBackButtonPress} />} Filter
      </div>
      <div className={Styles.content}>
        {!hasFilters ? (
          <PropertyFinder onPropertySelected={addFilter} properties={properties} className={Styles.propertyFinder} />
        ) : (
          <FilterList filters={filters} setFilters={setFilters} onRemovedAllFilters={onRemovedAllFilters} />
        )}
      </div>
    </div>
  )
}

export default Filter
